import "isomorphic-fetch";
import {
    Client,
    BatchRequestStep,
    GraphRequest
} from "@microsoft/microsoft-graph-client/lib/src/browser";
import authModule from "../../../utilities/AuthModule";
import { IWellKnownFolder } from "../interfaces/IWellKnownFolder";
import { WellKnownFolders } from "../config/graphConstants";
import { logger } from "../../../utilities/logger";
import { IMailFolder } from "../interfaces/IMailFolder";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { DocubirdCache } from "../config/constants";
import { getAuthenticatedClient } from "./GraphClientService";
import { escapeODataString } from "../../../utilities/EncodeDecode";
import { executeBatchWithRetry } from "./GraphBatchService";
import { messageHeaderSelectFields } from "./graphMessageFields";
import { selectedExchangeItemsStore } from "./selectedExchangeItemsStore";

const wellKnownFoldersStorageKey: string = "wellKnownFolders";
const folderStorageKey: string = "exchangeFolders";
const childFolderStorageKey: string = "exchangeChildFolders";

const MAX_PAGES: number = 20;

export interface IExchangeMessages {
    messages: any[];
    nextLink?: string;
}

const getAllPageValues = async (client: Client, request: GraphRequest): Promise<{ values: any[]; complete: boolean }> => {
    let response = await request.get();
    // A resolved response without an array `value` (e.g. a proxy-mangled 2xx) is
    // an anomaly, not an empty list; marking it incomplete keeps it out of the
    // session caches so the next call re-fetches instead of serving [] all session.
    const firstPageValid: boolean = Array.isArray(response?.value);
    const values: any[] = firstPageValid ? [...response.value] : [];

    let pageCount = 1;
    while (response?.["@odata.nextLink"]?.length > 0 && pageCount < MAX_PAGES) {
        response = await client.api(response["@odata.nextLink"]).get();
        if (Array.isArray(response?.value)) {
            values.push(...response.value);
        }
        pageCount++;
    }

    // A nextLink remaining after the page cap means the list was truncated;
    // callers must not cache such a result (nor an anomalous first page).
    return { values, complete: firstPageValid && !(response?.["@odata.nextLink"]?.length > 0) };
};

export const getWellKnownFolders = async (mailboxId: string): Promise<IWellKnownFolder[]> => {
    try {
        const cacheKey = `${wellKnownFoldersStorageKey}-${mailboxId}`
        const cachedWellKnownFolders = CacheHelper.get(cacheKey);
        if (cachedWellKnownFolders) {
            return cachedWellKnownFolders as IWellKnownFolder[];
        }

        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

        const batchRequests: BatchRequestStep[] = WellKnownFolders.map(wellKnownFolder => {
            return {
                id: wellKnownFolder.name,
                request: new Request(`/users/${mailboxId}/mailFolders/${wellKnownFolder.name}?$select=id`, {
                    method: "GET"
                })
            };
        });

        const { complete, stepResults } = await executeBatchWithRetry(client, batchRequests);

        const wellKnownFolderResults: IWellKnownFolder[] = [];
        for (const wellKnownFolder of WellKnownFolders) {
            const stepResult = stepResults.get(wellKnownFolder.name);
            const wellKnownFolderId: string = stepResult?.ok ? stepResult.body?.id : undefined;
            if (wellKnownFolderId) {
                wellKnownFolderResults.push({ id: wellKnownFolderId, name: wellKnownFolder.name, icon: wellKnownFolder.icon });
            }
        }

        // An incomplete (throttled) result must never be cached: a missing inbox id
        // would break folder auto-select and icons for the rest of the session.
        if (complete) {
            CacheHelper.set(cacheKey, wellKnownFolderResults);
        }

        return wellKnownFolderResults;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getWellKnownFolders)");
        return [];
    }
};

export const getExchangeFolders = async (mailboxId: string, filter: string, top?: number, saveToCache: boolean = true): Promise<IMailFolder[]> => {
    try {
        const cacheKey = `${folderStorageKey}-${mailboxId}`
        if (!filter) {
            const cachedFolders = CacheHelper.get(cacheKey);
            if (cachedFolders) {
                return cachedFolders as IMailFolder[];
            }
        }

        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

        const folderRequest: GraphRequest = client.api(`/users/${mailboxId}/mailFolders`)
            .top(top ? top : 999);

        if (filter) {
            folderRequest.filter(`contains(displayName, '${escapeODataString(filter)}')`);
        }

        // Child folders load lazily on expand (getExchangeChildFolders); the nav renders
        // expandability from childFolderCount, so no recursive tree walk is needed here.
        // An explicit top is a caller-bounded probe: return the first page as-is.
        let folders: IMailFolder[];
        let complete: boolean = false;
        if (top) {
            folders = (await folderRequest.get())?.value ?? [];
        } else {
            ({ values: folders, complete } = await getAllPageValues(client, folderRequest));
        }

        if (!filter && !top && saveToCache && complete) {
            CacheHelper.set(cacheKey, folders);
        }

        return folders;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getMailboxFolders)");
        return [];
    }
}

export const getExchangeChildFolders = async (mailboxId: string, folderId: string): Promise<IMailFolder[] | undefined> => {
    const cacheKey = `${childFolderStorageKey}-${mailboxId}-${folderId}`;
    const cachedChildFolders = CacheHelper.get(cacheKey);
    if (cachedChildFolders) {
        return cachedChildFolders as IMailFolder[];
    }

    try {
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const childFolderRequest: GraphRequest = client.api(`/users/${mailboxId}/mailFolders/${folderId}/childFolders`)
            .top(999);

        const { values: childFolders, complete } = await getAllPageValues(client, childFolderRequest);

        if (complete) {
            CacheHelper.set(cacheKey, childFolders);
        }
        return childFolders;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeChildFolders)");
        // undefined = fetch failed (caller may retry); [] = folder has no visible children.
        return undefined;
    }
}

export const getMailFolder = async (mailboxId: string, folderId: string): Promise<IMailFolder | undefined> => {
    try {
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        return await client.api(`/users/${mailboxId}/mailFolders/${folderId}`)
            .select("id,displayName,childFolderCount")
            .get();
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getMailFolder)");
        return undefined;
    }
}

export const getExchangeMessages = async (folderId: string, filter: string, orderBy: string, ascDesc: string, mailboxId: string, nextLink?: string, top: number = 50): Promise<IExchangeMessages> => {
    let result: any;

    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

        if (nextLink?.length > 0) {
            result = await client.api(nextLink).get();
        }
        else {
            // A small first page paints fast; the list's nextLink placeholder row
            // pages the remainder in on scroll.
            const request: GraphRequest = client.api(`/users/${mailboxId}/mailFolders/${folderId}/messages`)
                .select(`${messageHeaderSelectFields},bodyPreview,parentFolderId`)
                .top(top);

            if (filter && orderBy) { // https://learn.microsoft.com/en-us/graph/api/user-list-messages?view=graph-rest-1.0&tabs=http#using-filter-and-orderby-in-the-same-query
                let filterQuery: string = "";
                switch (orderBy) {
                    case "receivedDateTime":
                        filterQuery = "receivedDateTime ge 1900-01-01T00:00:00Z and ";
                        break;
                    case "sender/emailAddress/name":
                        filterQuery = "sender/emailAddress/name ne '' and ";
                        break;
                    default:
                        break;
                }

                if (filter.startsWith("@")) {
                    filter = escapeODataString(filter.replace("@", ""));
                    filterQuery += `(contains(sender/emailAddress/name, '${filter}') or contains(subject, '${filter}'))`;
                }
                else {
                    filterQuery += `contains(subject, '${escapeODataString(filter)}')`;
                }

                request.filter(filterQuery);
                request.orderby(`${orderBy} ${ascDesc}`);
            }
            else {
                if (filter) {
                    if (filter.startsWith("@")) {
                        filter = escapeODataString(filter.replace("@", ""));
                        request.filter(`contains(sender/emailAddress/name, '${filter}') or contains(subject, '${filter}')`);
                    }
                    else {
                        request.filter(`contains(subject, '${escapeODataString(filter)}')`);
                    }
                }
                if (orderBy) request.orderby(`${orderBy} ${ascDesc}`);
            }

            result = await request.get();
        }
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessages)");
        // Propagate next-page failures: returning the lenient empty result here would
        // make the caller overwrite its stored nextLink with undefined and permanently
        // end infinite scroll on a transient blip.
        if (nextLink?.length > 0) {
            throw error;
        }
    }

    return {
        messages: result?.value ?? [],
        nextLink: result ? result["@odata.nextLink"] : undefined
    }
}

export const getExchangeMessageHTMLBody = async (mailboxId: string, messageId: string): Promise<string> => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const message = await client.api(`/users/${mailboxId}/messages/${messageId}`)
            .select("body")
            .get();

        return message?.body?.content;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessageHTMLBody)");
    }
    return undefined;
}

export const getExchangeMessageAttachments = async (mailboxId: string, messageId: string): Promise<any[]> => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const messageAttachments = await client.api(`/users/${mailboxId}/messages/${messageId}/attachments`)
            .select("id,name,lastModifiedDateTime,isInline")
            .get();

        return Array.isArray(messageAttachments?.value) ? messageAttachments.value.filter(a => !a.isInline) : [];
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessageAttachments)");
    }
    return undefined;
}

const getExchangeMessage = async (mailboxId: string, messageId: string): Promise<any> => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const message = await client.api(`/users/${mailboxId}/messages/${messageId}`)
            .select(messageHeaderSelectFields)
            .get();
        return message;
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getExchangeMessageAttachments)");
    }
    return undefined;
}

export const getSelectedExchangeEmails = async (messageIds: string[], mailboxId: string, folderId: string) => {
    try {
        // const client = getAuthenticatedClient(await authModule.getMailboxAccessToken());
        const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
        const baseRequestURL: string = `/users/${mailboxId}/mailFolders/${folderId}/messages`;

        // Use synthetic, sequential batch ids. Graph treats the batch request id as
        // case-insensitive and rejects the whole batch (400) on duplicates; Exchange
        // message ids are case-sensitive base64 tokens that can collide case-insensitively.
        const batchRequests: BatchRequestStep[] = messageIds.map((messageId, index) => {
            return {
                id: `${index}`,
                request: new Request(`${baseRequestURL}/${messageId}/?$select=${messageHeaderSelectFields}`, {
                    method: "GET"
                })
            };
        });

        const { stepResults } = await executeBatchWithRetry(client, batchRequests);

        const messages: any[] = [];
        for (let index = 0; index < messageIds.length; index++) {
            const stepResult = stepResults.get(`${index}`);
            let message: any;
            if (stepResult?.ok) {
                message = stepResult.body;
            }
            else {
                // Residual (post-retry) failure: fall back to an individual GET, which the
                // Graph SDK middleware retries on its own for non-batched requests.
                console.warn("Exchange mail retrieval retry triggered: ", stepResult?.status);
                message = await getExchangeMessage(mailboxId, messageIds[index]);
            }

            // A message that could not be resolved at all still gets a stub entry:
            // the cached array must hold one entry per selected message so the
            // metadata form can never mistake a partial set for a smaller selection
            // (a reduction to one entry would direct-map that email's values onto
            // every file). Same invariant as OutlookService.getItems.
            messages.push(message ?? { id: messageIds[index] });
        }

        selectedExchangeItemsStore.set(messages);
    } catch (error) {
        logger(error, "ExchangeSerivce.ts (getSelectedExchangeEmails)");
    }
}

/**
 * Drops every session-cached folder structure (top-level lists, well-known ids,
 * lazily loaded children) so the next enumeration reflects folders created,
 * renamed, or deleted in Outlook since the cache was written.
 */
export const clearExchangeFolderCaches = (): void => {
    CacheHelper.removeByPrefix(`${folderStorageKey}-`);
    CacheHelper.removeByPrefix(`${wellKnownFoldersStorageKey}-`);
    CacheHelper.removeByPrefix(`${childFolderStorageKey}-`);
}